<template>
  <div :class="{ 'has-mutabakat-banner': hasPendingMutabakat }">
    <MutabakatDeadlineBanner v-if="userData" />

    <component :is="resolveLayoutVariant" :class="`skin-variant--${appSkinVariant}`">
      <transition :name="appRouteTransition" mode="out-in" appear>
        <router-view></router-view>
      </transition>
    </component>

    <MutabakatEnforceDialog v-if="userData" />
  </div>
</template>

<script>
import { getCurrentInstance, computed, ref, watch } from "@vue/composition-api";
import { useRouter } from "@core/utils";
import { useAppStore } from "@/stores/app";
import { getCustomerPendingMutabakat, getCustomerMutabakatApprovals } from "@/api/reconciliation";
import { postData } from "@utils";
import store from "@/store";
import orderStoreModule from "@/views/order/orderStoreModule";

import { provideToast } from "vue-toastification/composition";
import "vue-toastification/dist/index.css";

import LayoutBlank from "@/layouts/variants/blank/LayoutBlank.vue";
import LayoutCallCenter from "@/layouts/variants/call-center/LayoutCallCenter.vue";
import LayoutContentVerticalNav from "@/layouts/variants/content/vertical-nav/LayoutContentVerticalNav.vue";
import MutabakatEnforceDialog from "@/components/MutabakatEnforceDialog.vue";
import MutabakatDeadlineBanner from "@/components/MutabakatDeadlineBanner.vue";

import useAppConfig from "@core/@app-config/useAppConfig";

export default {
  components: {
    LayoutCallCenter,
    LayoutContentVerticalNav,
    LayoutBlank,
    MutabakatEnforceDialog,
    MutabakatDeadlineBanner,
  },
  setup() {
    const vm = getCurrentInstance().proxy;

    const { route } = useRouter();
    const { appSkinVariant, appRouteTransition } = useAppConfig();
    const appStore = useAppStore();
    // Route değişse de Vue'nun kendi reaktivitesiyle her zaman güncel kalır —
    // document.body üzerinde manuel classList manipülasyonu route geçişlerinde
    // tutarsız kalabildiği için bu şekilde bağlanıyor (bkz. :class kök div'de)
    const hasPendingMutabakat = computed(() =>
      (appStore.pendingMutabakat || []).some((item) => item.need_approve)
    );

    const userData = ref(JSON.parse(localStorage.getItem("userData")));

    if (userData.value) {
      appStore.userData = userData.value;
      appStore.userRole = userData.value.role;
    }

    const resolveLayoutVariant = computed(() => {
      if (route.value.meta.layout === "blank") return "layout-blank";
      if (route.value.meta.layout === "content") {
        if (vm.$ability.can("create", "callCenter")) {
          return "layout-call-center";
        } else {
          return `layout-content-vertical-nav`;
        }
      }

      return null;
    });

    provideToast({ timeout: 2500, position: "bottom-right" });

    // max_approve_date (YYYY-MM-DD) ile bugün arasındaki gün farkı — geriye
    // sayan "kaç gün kaldı" sayacı için kullanılır. Negatif/0 ise süre dolmuştur.
    const calcRemainingDays = (maxApproveDate) => {
      if (!maxApproveDate) return null;
      const deadline = new Date(`${maxApproveDate}T00:00:00`);
      if (Number.isNaN(deadline.getTime())) return null;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const msPerDay = 24 * 60 * 60 * 1000;
      return Math.round((deadline.getTime() - today.getTime()) / msPerDay);
    };

    // Süresi dolmuş (must_approve) mutabakat varken sepette kalan ürünlerin
    // sipariş verilme ihtimali yok — kafa karışıklığını önlemek için sessizce
    // boşaltılır. Kullanıcı başına oturumda bir kez yeterli (checkedCariKods).
    const PRODUCT_APP_STORE_MODULE_NAME = "app-order";
    const emptyBasketSilently = async () => {
      try {
        await postData({ method: "emptyBasket" });
        if (!store.hasModule(PRODUCT_APP_STORE_MODULE_NAME)) {
          store.registerModule(PRODUCT_APP_STORE_MODULE_NAME, orderStoreModule);
        }
        await store.dispatch("app-order/fetchBasketItemCount");
      } catch (e) {
        // Sessiz geçilir — sepet zaten sipariş oluşturma sırasında engellenir
      }
    };

    // Bekleyen mutabakat kontrolü — kullanıcı başına oturumda bir kez yeterli
    const checkedCariKods = new Set();
    const checkPendingMutabakat = async () => {
      const cariKod = userData.value?.cari_kod;
      if (!cariKod) return;
      if (checkedCariKods.has(cariKod)) return;

      try {
        const [pendingResponse, approvalsResponse] = await Promise.all([
          getCustomerPendingMutabakat(),
          getCustomerMutabakatApprovals(),
        ]);
        if (!pendingResponse || pendingResponse.error) return;

        // Backend'in bekleyen mutabakat listesi günde bir kez yenileniyor;
        // kullanıcı cevap verdikten sonra bile aynı gün içinde eski
        // (need_approve: true) veriyi döndürmeye devam edebiliyor. Onay
        // geçmişinde (approvalsResponse) kaydı geçen bir mutabakat_no zaten
        // cevaplanmış demektir, bekleyen listeden düşürülür.
        const answeredResult = !approvalsResponse?.error
          ? approvalsResponse?.response?.result
          : null;
        const answeredList = Array.isArray(answeredResult) ? answeredResult : [];
        const answeredNos = new Set(answeredList.map((item) => item.mutabakat_no));

        const result = pendingResponse.response?.result;
        const list = Array.isArray(result) ? result : result ? [result] : [];
        appStore.pendingMutabakat = list
          .filter((item) => item.need_approve && !answeredNos.has(item.mutabakat_no))
          .map((item) => {
            const remainingDays = calcRemainingDays(item.max_approve_date);
            return {
              ...item,
              remaining_days: remainingDays,
              // Backend'in must_approve bayrağı ile max_approve_date'e göre
              // hesapladığımız süre çelişirse, gerçek tarih kazanır
              must_approve:
                remainingDays !== null ? remainingDays <= 0 : item.must_approve,
            };
          });

        if (appStore.pendingMutabakat.some((item) => item.must_approve)) {
          emptyBasketSilently();
        }

        checkedCariKods.add(cariKod);
      } catch (e) {
        // Sessiz geçilir, bir sonraki route değişiminde tekrar denenir
      }
    };

    // Route değiştikçe (örn. login sonrası veya hesap değişimi) userData'yı
    // localStorage'dan taze oku
    watch(
      route,
      () => {
        const freshUserData = JSON.parse(localStorage.getItem("userData"));
        const userChanged =
          freshUserData?.cari_kod !== userData.value?.cari_kod;
        userData.value = freshUserData;

        if (freshUserData && userChanged) {
          appStore.userData = freshUserData;
          appStore.userRole = freshUserData.role;
          // Farklı bir hesapla login olunduğunda önceki kullanıcının
          // mutabakat verisi API'den taze yanıt gelene kadar bile gösterilmesin
          appStore.pendingMutabakat = [];
          checkedCariKods.clear();
        }

        if (freshUserData) {
          checkPendingMutabakat();
        }
      },
      { immediate: true }
    );

    return {
      resolveLayoutVariant,
      appSkinVariant,
      appRouteTransition,
      userData,
      hasPendingMutabakat,
    };
  },
};
</script>
